<script lang="ts">
  import type { PostStoryblok } from "$types/components";
  import { IconType } from "$types/atoms";
  import { onMount } from "svelte";

  import Icon from "$lib/atoms/Icon.svelte";
  import { story, headerVariant } from "$stores";

  import { page } from "$app/stores";
  import ButtonLink from "$lib/atoms/ButtonLink.svelte";
  import RichText from "$lib/molecules/RichText.svelte";
  import Section from "$lib/molecules/Section.svelte";
  import GridContainer from "$lib/molecules/GridContainer.svelte";
  import GridItem from "$lib/atoms/GridItem.svelte";
  import resizeImage from "$lib/utils/resizeImage";
  import { aspectRatio } from "$lib/utils/optimizeImage";

  export let blok: PostStoryblok;
  let postPreviewData = $page.data.postPreview;
  const currentPath = $page.params.slug;
  const parentPathArray = currentPath.split("/");
  parentPathArray.pop();
  const parentPath = "/" + parentPathArray.join("/");

  let isInsight = false;
  let hasMastheadImage = blok?.mastheadImage?.filename?.length > 0;
  let mastheadButton =
    blok?.mastheadButton?.length > 0 ? blok?.mastheadButton[0] : null;

  let url = ``;
  let facebookShareUrl = ``;
  let twitterShareUrl = ``;
  let linkedInShareUrl = ``;

  onMount(() => {
    headerVariant.set("default");
    url = window.location.href;
    facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      url,
    )}`;
    twitterShareUrl = `https://twitter.com/share?url=${encodeURIComponent(
      url,
    )}&text=${encodeURIComponent(blok.title)}`;
    linkedInShareUrl = `https://www.linkedin.com/shareArticle/?url=${encodeURIComponent(
      url,
    )}&mini=true`;
  });
</script>

<!-- Masthead -->
<Section class="!p-0" {blok}>
  <GridContainer>
    <GridItem col={12} colMd={12}>
      <section
        class="masthead mt-[20px] sm:mt-[40px] grid md:grid-cols-12 gap-x-[24px] gap-y-[30px] padding-bottom-100"
        style={postPreviewData?.category_name_plural !== "about"
          ? "border-bottom: 1px solid rgba(0, 0, 0, 0.1);"
          : ""}
      >
        <!-- Masthead Col Left -->
        <div
          class={!hasMastheadImage
            ? "col-span-12"
            : isInsight
              ? "md:col-span-7"
              : "md:col-span-6"}
        >
          <!-- Back link and share links -->
          <div class="flex items-center justify-end gap-4 mb-[40px]">
            {#if postPreviewData?.category_name_plural !== "about"}
              <div class="flex gap-3">
                <a
                  href={twitterShareUrl}
                  target="_blank"
                  rel="noreferrer"
                  class="flex justify-center items-center rounded-full bg-charcoal flex-[0_0_1.25rem] w-[1.25rem] h-[1.25rem] text-white"
                >
                  <div class="w-[50%]"><Icon type={IconType.TWITTER} /></div>
                </a>
                <a
                  href={linkedInShareUrl}
                  target="_blank"
                  rel="noreferrer"
                  class="flex justify-center items-center rounded-full bg-charcoal flex-[0_0_1.25rem] w-[1.25rem)] h-[1.25rem)] text-white"
                >
                  <div class="w-[50%]"><Icon type={IconType.LINKEDIN} /></div>
                </a>
              </div>
            {/if}
          </div>

          <div class="space-y-[16px] md:space-y-[30px]">
            <h1
              class={`text-charcoal ${
                !hasMastheadImage ? "md:max-w-[80%]" : "md:max-w-[93%]"
              } ${
                postPreviewData?.category_name_plural !== "about"
                  ? "type:h2"
                  : "type:h1"
              }`}
            >
              {@html $story?.story?.name}
            </h1>

            {#if blok?.mastheadContent?.content[0]?.content}
              <div>
                <RichText blok={{ content: blok?.mastheadContent }} />
              </div>
            {/if}

            {#if mastheadButton}
              <ButtonLink blok={mastheadButton} reduced />
            {/if}

            {#if isInsight}
              <div>INSIGHTS</div>
            {:else}
              <div class="flex items-center gap-4 md:gap-10">
                {#if blok?.topic}
                  <p class="type:h5-eyebrow text-charcoal">{blok?.topic}</p>
                {/if}
                {#if blok.author}
                  <p class="type:body-small text-charcoal/50">
                    {@html blok.author}
                  </p>
                {/if}
              </div>
            {/if}
          </div>
        </div>

        <!-- Masthead Col Right -->
        {#if hasMastheadImage}
          <div class={isInsight ? "md:col-span-5" : "md:col-span-6"}>
            <img
              class="md:max-w-[80%] xl:max-w-[70%] md:ml-auto rounded-md"
              src={resizeImage(blok.mastheadImage.filename, 900)}
              alt={blok.mastheadImage.alt}
              style={aspectRatio(blok.mastheadImage.filename)}
            />
          </div>
        {/if}
      </section>
    </GridItem>
  </GridContainer>
</Section>
